package com.siriusxm.pia.views.unifiedaggregator.services

import androidx.compose.runtime.Composable
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.pia.Application
import com.siriusxm.pia.ApplicationDescriptor
import com.siriusxm.pia.EntityLink
import com.siriusxm.pia.components.ButtonVariant
import com.siriusxm.pia.components.IconSize
import com.siriusxm.pia.components.button
import com.siriusxm.pia.components.icon
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.slugify
import contentingestion.unifiedmodel.EntityType
import kotlinx.browser.window
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.css.marginRight
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text

/**
 * Render an 'Open In...' button that integrates with other services to navigate to
 * the service page for the provided entity.
 */
@Composable
fun openInButton(
    context: AggregatorService, entityId: String?,
    entityType: EntityType, entityName: String? = null,
    typedEntityId: String? = null,
    fields: EntityLinkFields? = null,
    parentShowId: String? = null,
) {
    val defaultId: String? = entityId ?: typedEntityId
    // Configured application links can resolve without an atlas or typed id -- e.g. the Enrichment
    // Dashboard needs only the published entity's cutId (UCA-1277) -- so a failed id mapping hides only the entries that
    // need one, and the button is dropped only when nothing at all would be listed.
    val applicationLinks = Application.configuration.applications.orEmpty().flatMap { descriptor ->
        // Same gate as the application's own tile (UCA-1196): links into an application the
        // viewer can't open are hidden. Hide-only; the application enforces access itself.
        if (!Application.hasApplicationAccess(descriptor)) return@flatMap emptyList()
        descriptor.openInLinks().filter { link ->
            val types = link.types
            (types == null || types.contains(entityType.name)) &&
                hasResolvableEntityId(descriptor, link, entityType, entityId, typedEntityId, fields, parentShowId)
        }.map { descriptor to it }
    }
    if (defaultId == null && applicationLinks.isEmpty()) return
    button {
        this.title = "Open in..."
        this.variant = ButtonVariant.FLAT
        if (defaultId != null) subAction {
            this.content {
                Img(src = "images/sxm-logo-blue.png", "SiriusXM") {
                    style {
                        width(20.px)
                        property("vertical-align", "middle")
                        marginRight(3.px)
                    }
                }
                Text("SiriusXM Web Player")
            }
            this.title = "SXM web player"
            this.action {
                val url =
                    "https://siriusxm.com/player/${entityType.name}/${entityName?.slugify() ?: "slug"}/${entityId}"
                window.open(url, target = ATarget.Blank.targetStr)
            }
        }
        if (defaultId != null && (entityType == EntityType.CHANNEL_LINEAR || entityType == EntityType.CHANNEL_XTRA ||
            entityType == EntityType.LINEUP ||
            entityType == EntityType.SHOW ||
            entityType == EntityType.EPISODE_LINEAR || entityType == EntityType.EPISODE_AUDIO ||
            entityType == EntityType.ARTIST_STATION ||
            entityType == EntityType.TALENT
        )) {
            subAction {
                this.content {
                    icon("lists") {
                        size = IconSize.SMALL
                        style {
                            width(23.px)
                        }
                    }
                    Text("Channel Guide")
                }
                action {
                    when (entityType) {
                        EntityType.CHANNEL_LINEAR, EntityType.CHANNEL_XTRA -> {
                            context.globalNavigator.navigate("channelguide/channels/${entityId}")
                        }

                        EntityType.LINEUP -> {
                            context.globalNavigator.navigate("channelguide/lineups/${entityId}")
                        }

                        EntityType.SHOW -> {
                            context.globalNavigator.navigate("channelguide/shows/${entityId}")
                        }

                        EntityType.EPISODE_LINEAR, EntityType.EPISODE_AUDIO -> {
                            context.globalNavigator.navigate("channelguide/episodes/${entityId}")
                        }

                        EntityType.ARTIST_STATION -> {
                            context.globalNavigator.navigate("channelguide/artist-station/${entityId}")
                        }

                        EntityType.TALENT -> {
                            context.globalNavigator.navigate("channelguide/talent/${entityId}")
                        }

                        else -> {}
                    }
                }
            }
        }
        if (defaultId != null &&
            (entityType == EntityType.EVENT || entityType == EntityType.TEAM || entityType == EntityType.LEAGUE)
        ) {
            subAction {
                this.content {
                    icon("lists") {
                        size = IconSize.SMALL
                        style {
                            width(23.px)
                        }
                    }
                    Text("Sports")
                }
                action {
                    when (entityType) {
                        EntityType.LEAGUE -> {
                            context.globalNavigator.navigate("sports/league/${entityId}")
                        }

                        EntityType.TEAM -> {
                            context.globalNavigator.navigate("sports/team/${entityId}")
                        }

                        EntityType.EVENT -> {
                            context.globalNavigator.navigate("sports/event/${entityId}")
                        }

                        else -> {}
                    }
                }
            }
        }
        if (defaultId != null) subAction {
            this.content {
                Img(src = "images/dd-logo-rgb.png", "DataDog") {
                    style {
                        width(20.px)
                        property("vertical-align", "middle")
                        marginRight(3.px)
                    }
                }
                Text("Datadog")
            }
            action {
                val datadogBaseUrl = context.context.configuration.datadogLookupAtlasBusById
                val ddUrl = datadogBaseUrl.replace("{{ATLAS_ID}}", defaultId)
                window.open(ddUrl, target = ATarget.Blank.targetStr)
            }
        }

        applicationLinks.forEach { (it, link) ->
            subAction {
                this.content {
                    val image = openInLinkImage(it, link)
                    if (image != null) {
                        Img(src = image, link.name ?: it.name) {
                            style {
                                width(20.px)
                                property("vertical-align", "middle")
                                marginRight(3.px)
                            }
                        }
                    }
                    Text(link.name ?: it.name)
                }
                this.action {
                    // The Podcast Management Console's URL resolves a show's typed
                    // entity id directly, so no id calculation is needed. For an
                    // episode, typedEntityId is the EPISODE's own id -- not a show
                    // id -- so it's never a valid substitute; resolve the parent
                    // show's typed id via its known atlasId instead, or use nothing
                    // if that parent id isn't known.
                    val resolvedTypedEntityId = resolveEffectiveTypedEntityId(it, entityType, typedEntityId) {
                        parentShowId?.let { showId ->
                            runCatching { context.clientApi.getMapping(showId).sourceId }
                                .getOrNull()
                        }
                    }
                    val applicationEntityUrl = buildApplicationLinkUrl(
                        link,
                        entityId,
                        resolvedTypedEntityId,
                        fields
                    )
                    if (applicationEntityUrl != null) {
                        val consolePath = internalApplicationLinkPath(it, applicationEntityUrl)
                        if (consolePath != null) {
                            context.globalNavigator.navigate(consolePath)
                        } else {
                            window.open(
                                applicationEntityUrl,
                                target = ATarget.Blank.targetStr
                            )
                        }
                    }
                }
            }
        }
    }
}

/**
 * The icon for [link] in the "Open in..." menu: the link's own image (UCA-1277), else its
 * application's, or null for none.
 */
fun openInLinkImage(descriptor: ApplicationDescriptor, link: EntityLink): String? = link.image ?: descriptor.image

private val GUID_REGEX = Regex("^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$")

private val PLACEHOLDER_REGEX = Regex("""\{\{([^{}]*)\}\}""")

/**
 * Whether this descriptor's "Open in..." entry is for a podcast episode whose typed entity
 * id needs to be resolved from its parent show (the Podcast Management Console only knows
 * show ids, not episode ids). Shared by [hasResolvableEntityId] and `openInButton`'s click
 * handler so the two can't drift apart on which case gets the parent-show lookup.
 */
private fun isPodcastEpisodeLink(descriptor: ApplicationDescriptor, entityType: EntityType): Boolean =
    descriptor.name == "Podcasts" && entityType == EntityType.EPISODE_PODCAST

/**
 * The typed entity id to use for this descriptor's link: for a podcast episode, that's its
 * parent show's typed id (the Podcast Management Console only knows show ids), produced by
 * [episodeCaseTypedEntityId]; for everything else, the entity's own [typedEntityId]. Shared
 * by [hasResolvableEntityId] (which passes a synchronous approximation of the episode case)
 * and `openInButton`'s click handler (which resolves it for real) so the two can't drift
 * apart on which case gets the parent-show substitution.
 */
private inline fun resolveEffectiveTypedEntityId(
    descriptor: ApplicationDescriptor,
    entityType: EntityType,
    typedEntityId: String?,
    episodeCaseTypedEntityId: () -> String?,
): String? = if (isPodcastEpisodeLink(descriptor, entityType)) {
    episodeCaseTypedEntityId()
} else {
    typedEntityId
}

/**
 * Whether `openInButton` already knows, synchronously, that [link], one of [descriptor]'s
 * "Open in..." links, would resolve to a usable URL for the given entity. Used to hide a dead
 * "Open in..." entry instead of showing one that no-ops (or, before id resolution happens, would
 * open a broken URL) when clicked. For an episode-podcast entity this only confirms a parent show id is
 * known; the parent show's typed id itself is resolved asynchronously at click time and can
 * still fail then, which [buildApplicationLinkUrl] handles by failing closed.
 */
fun hasResolvableEntityId(
    descriptor: ApplicationDescriptor,
    link: EntityLink,
    entityType: EntityType,
    atlasId: String?,
    typedEntityId: String?,
    fields: EntityLinkFields?,
    parentShowId: String?,
): Boolean {
    val effectiveTypedEntityId =
        resolveEffectiveTypedEntityId(descriptor, entityType, typedEntityId) { parentShowId }
    return buildApplicationLinkUrl(link, atlasId, effectiveTypedEntityId, fields) != null
}

/**
 * The entity's own documents, which an "Open in..." link can read fields from by path (UCA-1277).
 *
 * @param data the aggregator entity's data, read by `{{data.<path>}}`
 * @param atlas the published (Atlas) entity, read by `{{atlas.<path>}}`; null until it loads
 */
data class EntityLinkFields(
    val data: JsonObject? = null,
    val atlas: JsonObject? = null,
)

/**
 * Take the path of an "Open in..." [link] and replace every `{{...}}` placeholder in it
 * to make a fully qualified url. A placeholder is one of the console's ids -- `ATLAS_ID`,
 * `SOURCE_ID`, `CUT_GUID` (the GUID in [typedEntityId], see [cutGuidFrom]) or `REPORTING_ID`
 * (shorthand for `data.reportingId`) -- or a field of the entity named by path (UCA-1277):
 * `{{data.reportingId}}` reads [EntityLinkFields.data], `{{atlas.cutId}}` reads
 * [EntityLinkFields.atlas], and nested fields use more dots (`{{atlas.show.id}}`). A placeholder
 * may end in a transform: `|guid` keeps only the GUID after any `<prefix>:` (see [cutGuidFrom]).
 *
 * Every value is URL-encoded, except `:`, which ids like `PC:35803` carry and URLs allow.
 *
 * Returns null -- rather than the entityPath with a placeholder left unsubstituted -- when any
 * placeholder is unknown, the field is missing or isn't a scalar, or a transform rejects the
 * value, so a failed/unavailable id resolution is a no-op instead of opening a broken URL with
 * the literal placeholder text still in it.
 */
fun buildApplicationLinkUrl(
    link: EntityLink,
    atlasId: String?,
    typedEntityId: String?,
    fields: EntityLinkFields? = null,
): String? {
    fun value(expression: String): String? {
        val key = expression.substringBefore("|").trim()
        val transform = expression.substringAfter("|", "").trim().takeIf { it.isNotEmpty() }
        val raw = when (key) {
            "ATLAS_ID" -> atlasId
            "SOURCE_ID" -> typedEntityId
            "CUT_GUID" -> cutGuidFrom(typedEntityId)
            "REPORTING_ID" -> fieldValue(fields?.data, "reportingId")
            else -> when (key.substringBefore(".")) {
                "data" -> fieldValue(fields?.data, key.substringAfter(".", ""))
                "atlas" -> fieldValue(fields?.atlas, key.substringAfter(".", ""))
                else -> {
                    console.warn("Open in... link ${link.path} uses an unknown placeholder {{$expression}}")
                    return null
                }
            }
        }
        return when (transform) {
            null -> raw
            "guid" -> cutGuidFrom(raw)
            else -> {
                console.warn("Open in... link ${link.path} uses an unknown transform |$transform")
                null
            }
        }
    }
    var unresolved = false
    val url = PLACEHOLDER_REGEX.replace(link.path) { match ->
        value(match.groupValues[1])?.let { encodeURIComponent(it).replace("%3A", ":") } ?: run {
            unresolved = true
            match.value
        }
    }
    return url.takeUnless { unresolved }
}

/**
 * The scalar at dotted [path] in [document] as a string (UCA-1277), or null when the path is empty,
 * any step is missing or not an object, or the value is null, an object or an array.
 */
internal fun fieldValue(document: JsonObject?, path: String): String? {
    if (path.isEmpty()) return null
    var element: JsonElement? = document
    for (step in path.split(".")) {
        element = (element as? JsonObject)?.get(step) ?: return null
    }
    return (element as? JsonPrimitive)?.takeUnless { it is JsonNull }?.content
}

/**
 * The cut GUID in a cut's typed entity id, which may carry a type prefix (`<prefix>:<guid>`), or null
 * when what follows the last `:` is not a GUID (UCA-1196). Mountain Services' request history only
 * looks cuts up by GUID, so a null here hides the "Open in..." entry instead of opening an empty page.
 */
fun cutGuidFrom(typedEntityId: String?): String? =
    typedEntityId?.substringAfterLast(":")?.trim()?.takeIf { GUID_REGEX.matches(it) }

/**
 * The console route to navigate to for a resolved [url], when [descriptor] is an application hosted in
 * the console, or null when the link should open in a new tab instead (UCA-1196). An internal
 * application's `entityPath` is relative to the application, so its stage-specific mount point comes
 * from the descriptor's own `path` rather than being repeated in every config.
 */
fun internalApplicationLinkPath(descriptor: ApplicationDescriptor, url: String): String? {
    if (!descriptor.internal() || url.startsWith("http")) return null
    return "${descriptor.path.trim('/')}/${url.removePrefix("/")}"
}
